import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import ActivityApi from "../../../api/instance/ActivityApi";

// Third-party packages.
import * as dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';
// Helpers.
import Helper from '../../../helpers/Helper';
import { getInstanceExecutions, getInstanceTotalTime } from "./utils";

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import HostTarget from '../../../types/host/HostTarget';
import PieChartSeries from '../../../types/PieChartSeries';
import { getErrorMessage, showError } from '../../../utils/showAlert';
import Statement from '../../../types/instance/Statement';
import Wait from '../../../types/instance/Wait';
import Change from '../../../types/instance/Change';
import Event from '../../../types/instance/Event';
import ChartPlotLine from '../../../types/ChartPlotLine'
import StatementsDataTable from '../../../types/instance/tables/StatementsDataTable';
import WaitsDataTable from '../../../types/instance/tables/WaitsDataTable';
import ChartOption from '../../../types/instance/ChartOption';
import Batch from "../../../types/instance/Batch";
import FilterOption from '../../../types/instance/FilterOption';
import ClientsDataTable from "../../../types/instance/tables/ClientsDataTable";
import Client from "../../../types/instance/Client";
import DatabasesDataTable from "../../../types/instance/tables/DatabasesDataTable";
import Database from "../../../types/instance/Database";
import SessionsDataTable from "../../../types/instance/tables/SessionsDataTable";
import Session from "../../../types/instance/Session";
import ProgramsDataTable from "../../../types/instance/tables/ProgramsDataTable";
import Program from "../../../types/instance/Program";
import UsersDataTable from "../../../types/instance/tables/UsersDataTable";
import User from "../../../types/instance/User";
import SchemasDataTable from "../../../types/instance/tables/SchemasDataTable";
import Schema from "../../../types/instance/Schema";
import OsUsersDataTable from "../../../types/instance/tables/OsUsersDataTable";
import OsUser from "../../../types/instance/OsUser";
import Alert from "../../../types/instance/Alert";

// Constants.
import { CHART_DATA_OPTION, chartOptionsList } from '../../../component/Constants';
import { INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_SQLSERVER } from '../../../component/Constants';
import { DATA_LOADING, DATA_FAILED, DATA_LOADED } from '../../../component/Constants';
import { CHART_COLOURS_STATEMENTS, CHART_COLOURS_WAITS } from '../../../component/Constants';

// Components.
import TimeChart from '../../../component/instance/chart/TimeChart';
import HostsOverTimeCharts from '../../../component/instance/chart/HostsOverTimeCharts';
import PieChart from '../../../component/PieChart';
import TablesWrapper from "../../../component/instance/table/TablesWrapper";
import GroupedViewToggle from "../../../component/instance/GroupedViewToggle";
import ActivityTabs from "../../../component/instance/Tabs";
import WidgetCard from "../../../component/instance/widget";
import StatementDetailsPanel from "./StatementDetailsPanel";
import ChatButton from '../../../component/chatGPT/ChatButton';
import Prompts from '../../../component/chatGPT/Prompts';

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(isoWeek.default);

interface IActivityProps {
    instance: InstanceTarget,
    instanceId: number,
    hosts: HostTarget[],
    batchId?: string,
    filterOptions: FilterOption[],
    filterParameters: string,
    chartPlotLines: ChartPlotLine[],
    changes: Change[],
    events: Event[],
    alerts: Alert[],
    setFilterOptions: Function,
}

const chartSeriesCeiling = Number(process.env.REACT_APP_CHART_SERIES_CEILING);

const TIME_CHART_STORAGE_KEY = 'activityTimeChart_selectedOption';

const Activity = (props: IActivityProps) => {
    const [loading, setLoading] = useState(DATA_LOADING);
    const [optionsList, setOptionsList] = useState(chartOptionsList);
    const [pieChartLoading, setPieChartLoading] = useState(true);
    const [totalInstanceTime, setTotalInstanceTime] = useState(0);
    const [totalPreviousInstanceTime, setTotalPreviousInstanceTime] = useState(0);
    const [executions, setExecutions] = useState(0);
    const [previousExecutions, setPreviousExecutions] = useState(0);
    const [timeChartDataOption, setTimeChartDataOption] = useState(CHART_DATA_OPTION.ACTIVITY);
    const [timeChartDataTitle, setTimeChartDataTitle] = useState('Activity Chart');
    const [currentTab, setCurrentTab] = useState('');

    const [displayHostMetrics, setDisplayHostMetrics] = useState<boolean>(false);

    // State for statement details panel
    const [selectedStatement, setSelectedStatement] = useState<string | null>(null);
    const [selectedStatementType, setSelectedStatementType] = useState<'statement' | 'groupedstatement' | 'batch' | 'groupedbatch'>('statement');
    const [panelOpen, setPanelOpen] = useState(false);

    const [batchesLength, setBatchesLength] = useState<number>(0);

    const [batches, setBatches] = useState<Batch[]>([]);
    const [statements, setStatements] = useState<Statement[]>([]);

    const [statementsDataTable, setStatementsDataTable] = useState<StatementsDataTable[]>([]);
    const [waitsDataTable, setWaitsDataTable] = useState<WaitsDataTable[]>([]);
    const [clientsDataTable, setClientDataTable] = useState<ClientsDataTable[]>([]);
    const [databasesDataTable, setDatabasesDataTable] = useState<DatabasesDataTable[]>([]);
    const [sessionsDataTable, setSessionsDataTable] = useState<SessionsDataTable[]>([]);
    const [programsDataTable, setProgramsDataTable] = useState<ProgramsDataTable[]>([]);
    const [usersDataTable, setUsersDataTable] = useState<UsersDataTable[]>([]);
    const [schemasDataTable, setSchemasDataTable] = useState<SchemasDataTable[]>([]);
    const [osUsersDataTable, setOsUsersDataTable] = useState<OsUsersDataTable[]>([]);

    const [statementsPieChartSeries, setStatementsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [waitsPieChartSeries, setWaitsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [batchesPieChartSeries, setBatchesPieChartSeries] = useState<PieChartSeries[]>([]);
    const [clientsPieChartSeries, setClientsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [databasesPieChartSeries, setDatabasesPieChartSeries] = useState<PieChartSeries[]>([]);
    const [programsPieChartSeries, setProgramsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [sessionsPieChartSeries, setSessionsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [usersPieChartSeries, setUsersPieChartSeries] = useState<PieChartSeries[]>([]);
    const [schemasPieChartSeries, setSchemasPieChartSeries] = useState<PieChartSeries[]>([]);
    const [osUsersPieChartSeries, setOsUsersPieChartSeries] = useState<PieChartSeries[]>([]);
    const [activityTimeSeries, setActivityTimeSeries] = useState<number[][]>([]);
    const [activityExecutionSeries, setActivityExecutionSeries] = useState<number[][]>([]);

    const {
        instance,
        hosts,
        chartPlotLines,
        changes,
        events,
        instanceId,
        alerts,
        setFilterOptions
    } = props;
    const timeRangeContext = useContext(TimeRangeContext);
    const { timeRange } = timeRangeContext;
    const isAutorefresh = timeRange.autorefresh

    useEffect(() => {
        setLoading(DATA_LOADING)

        setPieChartLoading(true)

        void getData();
    }, [instanceId, timeRange.from.valueOf(), timeRange.to.valueOf(), timeRange.interval, props.filterParameters]);

    useEffect(() => {
        let updatedList = [...chartOptionsList];

        updatedList.splice(3, 0, { label: 'Databases Chart', value: CHART_DATA_OPTION.DATABASES });

        if (instance.type === INSTANCE_TYPE_SQLSERVER) {
            updatedList.splice(2, 0, { label: "Batches Chart", value: CHART_DATA_OPTION.BATCHES });
        }

        if (instance.type === INSTANCE_TYPE_ORACLE) {
            updatedList.splice(updatedList.findIndex(({ value }) => value === CHART_DATA_OPTION.DATABASES) + 1, 0, { label: 'Schemas Chart', value: CHART_DATA_OPTION.SCHEMAS });
            updatedList.splice(updatedList.findIndex(({ value }) => value === CHART_DATA_OPTION.USERS) + 1, 0, { label: 'OS Users Chart', value: CHART_DATA_OPTION.OS_USERS });
        }

        setOptionsList(updatedList);

        // Restore the previously selected top chart, if it's still valid for this instance type.
        try {
            const savedOption = localStorage.getItem(TIME_CHART_STORAGE_KEY);
            if (savedOption !== null) {
                const matchedOption = updatedList.find(({ value }) => String(value) === savedOption);
                if (matchedOption) {
                    setTimeChartDataOption(matchedOption.value);
                    setTimeChartDataTitle(matchedOption.label);
                } else {
                    localStorage.removeItem(TIME_CHART_STORAGE_KEY);
                }
            }
        } catch (error) {
            console.warn('Failed to restore top chart selection from localStorage:', error);
        }
    }, [instance.type]);

    const getData = async () => {
        try {
            setLoading(DATA_LOADING);
            setPieChartLoading(true);

            if (!isAutorefresh) {
                // Reset all data states to ensure fresh data
                setStatements([]);
                setStatementsDataTable([]);
                setStatementsPieChartSeries([]);
                setWaitsDataTable([]);
                setWaitsPieChartSeries([]);
                setClientDataTable([]);
                setClientsPieChartSeries([]);
                setDatabasesDataTable([]);
                setDatabasesPieChartSeries([]);
                setSessionsDataTable([]);
                setSessionsPieChartSeries([]);
                setProgramsDataTable([]);
                setProgramsPieChartSeries([]);
                setUsersDataTable([]);
                setUsersPieChartSeries([]);
                setSchemasDataTable([]);
                setSchemasPieChartSeries([]);
                setOsUsersDataTable([]);
                setOsUsersPieChartSeries([]);
                setBatches([]);
                setBatchesPieChartSeries([]);
                setTotalInstanceTime(0);
                setTotalPreviousInstanceTime(0);
                setExecutions(0);
                setPreviousExecutions(0);
            }
            const requests: Promise<unknown>[] = [
                getStatements(instanceId),
                ...(instance.type === INSTANCE_TYPE_SQLSERVER ? [getBatches(instanceId)] : []),
                getInstanceTime(instanceId),
                ...(props.filterParameters ? [] : [getExecutions(instanceId)]),
                getWaits(instanceId),
                getClients(instanceId),
                getDatabases(instanceId),
                getSessions(instanceId),
                getPrograms(instanceId),
                getUsers(instanceId),
                ...(instance.type === INSTANCE_TYPE_ORACLE ? [getSchemas(instanceId), getOsUsers(instanceId)] : []),
            ];

            const results = await Promise.allSettled(requests);
            const failures = results.filter(result => result.status === 'rejected');

            if (failures.length) {
                const reason = (failures[0] as PromiseRejectedResult).reason;
                showError('Failed to load activity data', getErrorMessage(reason));
            }

            setLoading(failures.length === results.length ? DATA_FAILED : DATA_LOADED);
            setPieChartLoading(false);
        } catch (error) {
            showError('Failed to load activity data', getErrorMessage(error));
            setLoading(DATA_FAILED);
            setPieChartLoading(false);
        }
    };

    const getInstanceTime = async (instanceId: number) => {
        const executionsTimeResult = await getInstanceTotalTime(instanceId, timeRangeContext, props.filterParameters)
        setTotalInstanceTime(executionsTimeResult.totalInstanceTime)
        setTotalPreviousInstanceTime(executionsTimeResult.totalPreviousInstanceTime)
    }

    const getExecutions = async (instanceId: number) => {
        const executionsResult = await getInstanceExecutions(instanceId, timeRangeContext, props.filterParameters)
        setExecutions(executionsResult.executions)
        setPreviousExecutions(executionsResult.previousExecutions)
    }

    const getStatements = async (instanceId: number) => {
        try {
            const response = await ActivityApi.getSql(buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(a.sqlhash) || 0;
                const bHash = Number(b.sqlhash) || 0;

                return aHash - bHash;
            });

            let statementsDataTable: StatementsDataTable[] = [],
                    topStatementsChartSeries: PieChartSeries[] = [];
            // Build top statements pie chart.
            for (let index = 0; index < response.data.length; index++) {

                if (index < CHART_COLOURS_STATEMENTS.length) {
                    // Set the top colours for the chart.
                    response.data[index].color = CHART_COLOURS_STATEMENTS[index];
                }
                statementsDataTable.push({
                    id: index,
                    color: ((index < CHART_COLOURS_STATEMENTS.length) ? CHART_COLOURS_STATEMENTS[index] : ''),
                    sqlhash: response.data[index].sqlhash,
                    sqltext: response.data[index].sqltext.slice(0, 1000),
                    executions: ((response.data[index].executions === undefined) ? null : Number(response.data[index].executions)),
                    waittime: response.data[index].waittime,
                    averagetime: ((response.data[index].executions === undefined) ? null : Number((response.data[index].waittime / Number(response.data[index].executions)).toFixed(2))),
                    statement: response.data[index]
                });

                topStatementsChartSeries.push({
                    color: response.data[index].color,
                    formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    name: ((response.data[index].sqlhash !== null) ? response.data[index].sqlhash : '-'),
                    y: response.data[index].waittime,
                    url: ((response.data[index].sqlhash !== null && response.data[index].sqlhash !== '-') ? `/instances/${instanceId}/activity/statement/${response.data[index].sqlhash}` : null)
                });
            }

            if (response.data.length > chartSeriesCeiling) {

                // Get and add a remaining category to the chart.
                const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                        .map((item: Statement) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);

                topStatementsChartSeries.push({
                    color: '#5e4fa2',
                    formatted: Helper.getTimeInEnglish(remainingTime),
                    name: 'Other Statements',
                    y: remainingTime,
                    url: null
                });
            }
            setStatements(response.data)
            setStatementsDataTable(statementsDataTable)
            setStatementsPieChartSeries(topStatementsChartSeries.slice(0, chartSeriesCeiling))

        } catch (error) {
            console.error('Error fetching statements:', error);
            throw error;
        }
    }

    const getBatches = async (instanceId: number) => {
        try {
            const response = await ActivityApi.getBatch(buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            let topBatchesChartSeries: PieChartSeries[] = [];

            response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(a.batchsqlhash) || 0;
                const bHash = Number(b.batchsqlhash) || 0;

                return aHash - bHash;
            });

            // Build top batches pie chart.
            for (let index = 0; index < response.data.length &&
            index < CHART_COLOURS_STATEMENTS.length; index++) {
                topBatchesChartSeries.push({
                    color: CHART_COLOURS_STATEMENTS[index],
                    formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    name: ((response.data[index].batchsqlhash !== null) ? response.data[index].batchsqlhash : '-'),
                    y: response.data[index].waittime,
                    url: ((response.data[index].batchsqlhash !== null && response.data[index].batchsqlhash !== '-') ? `/instances/${instanceId}/activity/batch/${response.data[index].batchsqlhash}` : null)
                });
            }

            if (response.data.length > chartSeriesCeiling) {

                // Get and add a remaining category to the chart.
                const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                        .map((item: Batch) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);

                topBatchesChartSeries.push({
                    color: '#5e4fa2',
                    formatted: Helper.getTimeInEnglish(remainingTime),
                    name: 'Other Statements',
                    y: remainingTime,
                    url: null
                });
            }
            setBatches(response.data)
            setBatchesPieChartSeries(topBatchesChartSeries.slice(0, chartSeriesCeiling))
        } catch (error) {
            console.error('Error fetching batches:', error);
            throw error;
        }
    }

    const getWaits = async (instanceId: number) => {
        try {
            const response = await ActivityApi.getWaitEvents(buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            let waitsDataTable: WaitsDataTable[] = [],
                    topWaitsChartSeries: PieChartSeries[] = [];

            response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                return a.waitevent.localeCompare(b.waitevent);
            });

            // Build top waits pie chart.
            for (let index = 0; index < response.data.length; index++) {

                if (index < CHART_COLOURS_WAITS.length) {
                    // Set the top colours for the chart.
                    response.data[index].color = CHART_COLOURS_WAITS[index];
                }

                waitsDataTable.push({
                    id: index,
                    color: response.data[index].color,
                    waitevent: response.data[index].waitevent,
                    waittime: response.data[index].waittime
                });

                topWaitsChartSeries.push({
                    color: response.data[index].color,
                    formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    name: response.data[index].waitevent,
                    y: response.data[index].waittime
                });
            }

            if (response.data.length > chartSeriesCeiling) {

                // Get and add a remaining category to the chart.
                const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                        .map((item: Wait) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);

                topWaitsChartSeries.push({
                    color: CHART_COLOURS_WAITS[chartSeriesCeiling],
                    formatted: Helper.getTimeInEnglish(remainingTime),
                    name: 'Other Waits',
                    y: remainingTime
                });
            }
            setWaitsDataTable(waitsDataTable)
            setWaitsPieChartSeries(topWaitsChartSeries.slice(0, chartSeriesCeiling))
        } catch (error) {
            console.error('Error fetching waits:', error);
            throw error;
        }
    }

    const fetchData = async (endpoint: string, instanceId: number, setDataTable: Function, setPieChartSeries: Function, transformData: (item: any) => {
        name: string,
        waittime: number,
        key: string
    }) => {
        let data: any[] = [], pieChartSeries: PieChartSeries[] = [];

        try {
            const response = await ActivityApi.getDimension(endpoint, buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);
            const sortedData = response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait; // Descending order for waittime
                }

                const key = transformData(a).key;
                const aVal = a[key];
                const bVal = b[key];

                // Handle sorting for numbers and strings
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return aVal - bVal; // Numeric comparison
                }

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return aVal.localeCompare(bVal); // String comparison
                }

                // If one is number and the other is string, put numbers first
                return (typeof aVal === 'number' ? -1 : 1);
            });

            sortedData.forEach((item: any, index: number) => {
                const { name, waittime, key } = transformData(item);

                data.push({
                    id: index,
                    color: CHART_COLOURS_STATEMENTS[index],
                    [key]: name,
                    waittime
                });

                if (index < Number(process.env.REACT_APP_CHART_SERIES_CEILING)) {
                    pieChartSeries.push({
                        color: CHART_COLOURS_STATEMENTS[index],
                        formatted: Helper.getTimeInEnglish(waittime),
                        name,
                        y: waittime
                    });
                }
            });
            setDataTable(data);
            setPieChartSeries(pieChartSeries);
        } catch (error) {
            console.error(`Failed to retrieve ${endpoint}.`, error);
            // Set empty arrays to prevent stale data
            setDataTable([]);
            setPieChartSeries([]);
            throw error;
        }
    };

    const getDatabases = async (instanceId: number) => {
        return fetchData(
                'database',
                instanceId,
                setDatabasesDataTable,
                setDatabasesPieChartSeries,
                (item: Database) => ({
                    name: item.database || '-',
                    waittime: item.waittime,
                    key: 'database'
                })
        );
    };

    const getSessions = async (instanceId: number) => {
        return fetchData(
                'session',
                instanceId,
                setSessionsDataTable,
                setSessionsPieChartSeries,
                (item: Session) => ({
                    name: item.session || '-',
                    waittime: item.waittime,
                    key: 'session'
                })
        );
    };

    const getPrograms = async (instanceId: number) => {
        return fetchData(
                'program',
                instanceId,
                setProgramsDataTable,
                setProgramsPieChartSeries,
                (item: Program) => ({
                    name: item.program || '-',
                    waittime: item.waittime,
                    key: 'program'
                })
        );
    };

    const getUsers = async (instanceId: number) => {
        return fetchData(
                'username',
                instanceId,
                setUsersDataTable,
                setUsersPieChartSeries,
                (item: User) => ({
                    name: item.username || '-',
                    waittime: item.waittime,
                    key: 'username'
                })
        );
    };

    const getSchemas = async (instanceId: number) => {
        return fetchData(
                'schema',
                instanceId,
                setSchemasDataTable,
                setSchemasPieChartSeries,
                (item: Schema) => ({
                    name: item.schema || '-',
                    waittime: item.waittime,
                    key: 'schema'
                })
        );
    };

    const getOsUsers = async (instanceId: number) => {
        return fetchData(
                'osuser',
                instanceId,
                setOsUsersDataTable,
                setOsUsersPieChartSeries,
                (item: OsUser) => ({
                    name: item.osuser || '-',
                    waittime: item.waittime,
                    key: 'osuser'
                })
        );
    };

    const getClients = async (instanceId: number) => {
        return fetchData(
                'client',
                instanceId,
                setClientDataTable,
                setClientsPieChartSeries,
                (item: Client) => ({
                    name: item.client || '-',
                    waittime: item.waittime,
                    key: 'client'
                })
        );
    };

    const toggleTimeChartData = (timeChartDataOption: number) => {
        const selectedOption = optionsList.find(({ value }) => value === timeChartDataOption);
        const timeChartDataTitle = selectedOption?.label || "Unknown Chart";

        setTimeChartDataOption(timeChartDataOption);
        setTimeChartDataTitle(timeChartDataTitle);

        try {
            localStorage.setItem(TIME_CHART_STORAGE_KEY, String(timeChartDataOption));
        } catch (error) {
            console.warn('Failed to save top chart selection to localStorage:', error);
        }
    };

    const toggleHostMetricChart = () => {
        setDisplayHostMetrics(!displayHostMetrics)
    }

    const setBatchesOptions = (batchesLength: number, batchesPieChartSeries: PieChartSeries[]) => {
        setBatchesLength(batchesLength)
        setBatchesPieChartSeries(batchesPieChartSeries)
    }

    const openPanel = (statementId: string, statementType: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch' = 'statement') => {
        setSelectedStatement(statementId);
        setSelectedStatementType(statementType);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedStatement(null);
    };

    let pieChartOptions: ChartOption[] = [
        { optionId: 0, key: 'Statements', data: statementsPieChartSeries },
    ];

    if (instance.type === INSTANCE_TYPE_SQLSERVER) {
        pieChartOptions.push({ optionId: 0, key: 'Batches', data: batchesPieChartSeries });
    }

    pieChartOptions.push(
            { optionId: 0, key: 'Waits', data: waitsPieChartSeries },
            { optionId: 0, key: 'Databases', data: databasesPieChartSeries },
            ...(instance.type === INSTANCE_TYPE_ORACLE ? [{ optionId: 0, key: 'Schemas', data: schemasPieChartSeries }] : []),
            { optionId: 0, key: 'Sessions', data: sessionsPieChartSeries },
            { optionId: 0, key: 'Clients', data: clientsPieChartSeries },
            { optionId: 0, key: 'Users', data: usersPieChartSeries },
            ...(instance.type === INSTANCE_TYPE_ORACLE ? [{ optionId: 0, key: 'OS Users', data: osUsersPieChartSeries }] : []),
            { optionId: 0, key: 'Programs', data: programsPieChartSeries }
    );

    const pieChartComponents = useMemo(() => {
        return (
                <div className="row row-cols-1 row-cols-lg-2">
                            <PieChart
                                    filtersOptions={props.filterOptions}
                                    uniqueIndex={0}
                                    chartOptions={pieChartOptions}
                                    loading={pieChartLoading}
                                    instanceType={instance.type}
                                    // @ts-ignore
                                    defaultChartOption={pieChartOptions.find(option => option.key === 'Statements')}
                                    setFilterOptions={props.setFilterOptions}
                            />


                            <PieChart
                                    filtersOptions={props.filterOptions}
                                    uniqueIndex={1}
                                    chartOptions={pieChartOptions}
                                    loading={pieChartLoading}
                                    instanceType={instance.type}
                                    // @ts-ignore
                                    defaultChartOption={pieChartOptions.find(option => option.key === 'Waits')}
                                    setFilterOptions={props.setFilterOptions}
                            />
                </div>
        );
    }, [
        pieChartLoading,
        pieChartOptions,
        props.filterOptions,
        instance.type,
        props.setFilterOptions,
        batchesPieChartSeries,
        clientsPieChartSeries,
        databasesPieChartSeries,
        programsPieChartSeries,
        statementsPieChartSeries,
        sessionsPieChartSeries,
        usersPieChartSeries,
        schemasPieChartSeries,
        osUsersPieChartSeries,
        waitsPieChartSeries,
    ]);

    return (
            <React.Fragment>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 flexlist">
                    <WidgetCard
                            name="Total Time"
                            type='time'
                            iconClassName="time"
                            icon="fa-stopwatch"
                            value={totalInstanceTime}
                            previous={totalPreviousInstanceTime}
                            cardTooltipText="The total time spent"
                    />
                    {!props.filterParameters && (
                            <>
                                <WidgetCard
                                        name="Executions"
                                        iconClassName="executions"
                                        icon="fa-chart-line"
                                        value={executions}
                                        previous={previousExecutions}
                                        cardTooltipText="The total number of executions"
                                />
                                <WidgetCard
                                        name="Average Time"
                                        type='time'
                                        icon="fa-clock"
                                        iconClassName="averagetime"
                                        value={executions > 0 ? totalInstanceTime / executions : 0}
                                        previous={previousExecutions > 0 ? totalPreviousInstanceTime / previousExecutions : undefined}
                                        cardTooltipText="The average time per execution"
                                />
                            </>
                    )}
                    <WidgetCard
                            name="Events"
                            events={{
                                alerts: alerts.length,
                                changes: changes.length,
                                custom: events.length
                            }}
                    />
                </div>

                {/* Main Content Widgets */}
                <div className="row">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                                Time spent
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapseExecutions" aria-expanded="false"
                                   aria-controls="collapseExecutions"/>
                                <div className="btn-group float-end">
                                    <GroupedViewToggle section="activity" instanceId={instance.id} isGroup={false} tab="time-spent"/>
                                    <ChatButton
                                        className="btn-ai-copilot float-end ms-1"
                                        label="Ask AI Co-pilot"
                                        value={Prompts.getActivityOverviewPrompt(
                                            instance.type,
                                            timeRangeContext.timeRange.from.format('DD MMM HH:mm'),
                                            timeRangeContext.timeRange.to.format('DD MMM HH:mm'),
                                            timeRangeContext.timeRange.timezone,
                                            totalInstanceTime,
                                            executions,
                                            activityTimeSeries,
                                            activityExecutionSeries,
                                            statementsDataTable,
                                            waitsDataTable,
                                            usersDataTable,
                                            programsDataTable,
                                            sessionsDataTable,
                                            databasesDataTable,
                                            clientsDataTable,
                                            changes,
                                            alerts,
                                            events
                                        )}
                                    />
                                    {hosts.length > 0 && (
                                            <button type="button" className="btn btn-xsm btn-primary float-end ms-1"
                                                    onClick={() => toggleHostMetricChart()}>
                                                {((displayHostMetrics) ? 'Hide Hosts' : 'Show Hosts')}
                                            </button>
                                    )}

                                    <button id="btnChartList" type="button"
                                            className="btn btn-xsm btn-primary dropdown-toggle float-end ms-1"
                                            data-bs-toggle="dropdown" aria-expanded="false">
                                        {timeChartDataTitle}
                                    </button>
                                    <ul className="dropdown-menu border" aria-labelledby="btnChartList">
                                        <li>
                                            {optionsList.map(({ label, value }) => (
                                                    <span
                                                            key={value}
                                                            className={`dropdown-item ${timeChartDataOption === value ? "active" : ""}`}
                                                            role="button"
                                                            onClick={() => toggleTimeChartData(value)}
                                                    >
                                                        {label}
                                                    </span>
                                            ))}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div id="collapseExecutions" className="card-body collapse show">
                                <TimeChart dataOption={timeChartDataOption}
                                           instanceId={instanceId}
                                           plotLines={chartPlotLines}
                                           waits={waitsDataTable}
                                           clients={clientsDataTable}
                                           programs={programsDataTable}
                                           sessions={sessionsDataTable}
                                           databases={databasesDataTable}
                                           schemas={schemasDataTable}
                                           users={usersDataTable}
                                           osUsers={osUsersDataTable}
                                           statements={statements}

                                           batches={batches}
                                           filters={props.filterParameters}
                                           onActivityDataLoad={({ timeSeries, executionSeries }) => {
                                               setActivityTimeSeries(timeSeries);
                                               setActivityExecutionSeries(executionSeries);
                                           }}
                                />
                                {displayHostMetrics && (
                                        <HostsOverTimeCharts instance={instance} hosts={hosts}
                                                            
                                                             plotLines={chartPlotLines}/>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {pieChartComponents}

                <div className="row row-cols-1">

                    {/* Tab Headings */}
                    <ActivityTabs
                            instanceType={instance.type}
                            batchesLength={batchesLength}
                            waitsLength={waitsDataTable.length}
                            clientsLength={clientsDataTable.length}
                            setCurrentTab={setCurrentTab}
                            databasesLength={databasesDataTable.length}
                            sessionsLength={sessionsDataTable.length}
                            usersLength={usersDataTable.length}
                            schemasLength={schemasDataTable.length}
                            osUsersLength={osUsersDataTable.length}
                            programsLength={programsDataTable.length}
                            statementsLength={statementsDataTable.length}
                            isGroup={false}
                            filterOptions={props.filterOptions}
                    />
                    <TablesWrapper instance={instance}
                                   isGroup={false}
                                   currentTab={currentTab}
                                   loading={loading}
                                   totalInstanceTime={totalInstanceTime}
                                   statementsDataTable={statementsDataTable}
                                   databasesDataTable={databasesDataTable}
                                   sessionsDataTable={sessionsDataTable}
                                   programsDataTable={programsDataTable}
                                   usersDataTable={usersDataTable}
                                   schemasDataTable={schemasDataTable}
                                   osUsersDataTable={osUsersDataTable}
                                   waitsDataTable={waitsDataTable}
                                   clientsDataTable={clientsDataTable}
                                   setFilterOptions={setFilterOptions}
                                   setBatchesOptions={setBatchesOptions}
                                   onStatementClick={openPanel}
                    />
                </div>

                {/* Statement Details Slide-out Panel */}
                {selectedStatement && (
                    <StatementDetailsPanel
                        key={selectedStatement}
                        statementId={selectedStatement}
                        open={panelOpen}
                        instance={instance}
                        onClose={closePanel}
                        statementType={selectedStatementType}
                        onStatementClick={openPanel}
                    />
                )}

            </React.Fragment>
    );
};

Activity.propTypes = {
    setFilterOptions: PropTypes.func.isRequired
};

export default Activity;
